import assetUtils, { Asset } from 'app-lib/assetUtils'
import { BigNumber } from '@0x/utils'
import { FulcrumProvider } from '../../../fulcrum/src/services/FulcrumProvider'
import appConfig, { stakingConfig } from '../config/appConfig'
import { ChainId } from '../config/chains/chainCommon'
import { stakeableToken, unstakeableToken } from '../domain/staking/stakingTypes'
import providerUtils from './providerUtils'

interface ITokenBalances {
  ooki: BigNumber
  vbzrx: BigNumber
  iooki: BigNumber
  slp_ooki_eth: BigNumber
}

/**
 * Checks if the amounts to be staked are valid.
 * @param userBalances The balances of unstaked tokens in user wallet
 * @param tokensToStake The amount of tokens to be staked
 */
function verifyStake(userBalances: ITokenBalances, tokensToStake: ITokenBalances) {
  const { ooki, vbzrx, iooki, slp_ooki_eth } = tokensToStake

  // All zeros
  if (ooki.eq(0) && vbzrx.eq(0) && iooki.eq(0) && slp_ooki_eth.eq(0)) {
    return false
  }
  // Some negative
  if (ooki.isNegative() || vbzrx.isNegative() || iooki.isNegative() || slp_ooki_eth.isNegative()) {
    return false
  }
  // Some bigger than total balance
  if (
    ooki.gt(userBalances.ooki) ||
    vbzrx.gt(userBalances.vbzrx) ||
    iooki.gt(userBalances.iooki) ||
    slp_ooki_eth.gt(userBalances.slp_ooki_eth)
  ) {
    return false
  }
  return true
}

/**
 * An helper to get contracts and data related to staking easily.
 * - currentAccount can be '' so we dont want that
 * - need web3Wrapper to send transactions
 * Throws if any dependency is missing.
 */
function getDependenciesStrict(provider: FulcrumProvider) {
  const deps = providerUtils.getDependenciesStrict(provider)
  const stakingContract = deps.contractsSource.getStakingContract()
  const voteDelegatorContract = deps.contractsSource.getVoteDelegatorContract()
  return { ...deps, stakingContract, voteDelegatorContract }
}

/**
 * Set the spending allowance of tokens by the staking contract (so that user can stake)
 * This is expected to be called for ooki, vbzrx, iooki and/or slp
 */
async function setStakingAllowance(
  provider: FulcrumProvider,
  asset: keyof typeof Asset,
  amount: BigNumber
) {
  const { contractsSource, userAddress } = getDependenciesStrict(provider)
  const erc20Address = assetUtils.getAddress(provider.currentChainId, asset as Asset)
  const erc20Contract = contractsSource.getErc20Contract(erc20Address)

  const txHash = await erc20Contract
    .approve(stakingConfig.stakingContract, amount)
    .sendTransactionAsync({
      from: userAddress,
    })

  return providerUtils.waitForTransactionMined(provider, txHash)
}

async function getVestedBzrxBalance(provider: FulcrumProvider) {
  const { contractsSource, userAddress } = getDependenciesStrict(provider)
  const vbzrxContract = contractsSource.getBzrxVestingContract()
  return vbzrxContract.vestedBalanceOf(userAddress).callAsync()
}

/**
 * Mostly for users who hold vbzrx in their wallet and just want to claim their bzrx
 */
function claimVestedBZRX(provider: FulcrumProvider) {
  const { contractsSource } = getDependenciesStrict(provider)
  const vbzrxContract = contractsSource.getBzrxVestingContract()
  const tx = vbzrxContract.claim()
  return providerUtils.runTx(provider, tx, vbzrxContract)
}

/**
 * User gets 5 types of rewards (earnings)
 * `[ooki, stableCoin, ookiVesting, stableCoinVesting, sushi]`
 * NOTE 1: ooki amount is actually the sum of "real" staking rewards + vested staked vbzrx
 * NOTE 2: SUSHI rewards are coming from user having staked SLP (Sushi LP)
 */
const getStakingRewards = async (
  provider: FulcrumProvider
): Promise<{
  ooki: BigNumber
  stableCoin: BigNumber
  ookiVesting: BigNumber
  stableCoinVesting: BigNumber
  sushi: BigNumber
}> => {
  const { userAddress, stakingContract } = getDependenciesStrict(provider)

  // [ooki, stableCoin, ookiVesting, stableCoinVesting, sushi]
  const result = await stakingContract.earned(userAddress).callAsync({
    from: userAddress,
  })

  return {
    ooki: result[0].div(10 ** 18),
    stableCoin: result[1].div(10 ** 18),
    ookiVesting: result[2].div(10 ** 18), // TODO
    stableCoinVesting: result[3].div(10 ** 18),
    sushi: result[4].div(10 ** 18),
  }
}

/**
 * Part of the staking rewards that are actually vested OOKI coming from staked vbzrx
 */
async function getVestedOokiInRewards(provider: FulcrumProvider, vbzrxStaked: BigNumber) {
  const { userAddress, stakingContract, web3Wrapper } = getDependenciesStrict(provider)

  const vestingLastSyncTime = await stakingContract.vestingLastSync(userAddress).callAsync({
    from: userAddress,
  })
  /**
   * Note: it is better to use the blockchain time than the user browser local time
   */
  const lastBlockTime = await web3Wrapper.getBlockTimestampAsync('latest')
  const now = new BigNumber(lastBlockTime)

  const vestedOokiInRewards = now
    .minus(vestingLastSyncTime)
    .div(appConfig.vestingDurationAfterCliff)
    .times(vbzrxStaked)
    .times(10)

  return vestedOokiInRewards
}

/**
 * Returns the amount of staked tokens of a user.
 *
 * The amounts are in base units so they must be divided by 10 ** decimals
 * @param address the wallet of the user
 */
async function getStakedBalances(provider: FulcrumProvider) {
  const { stakingContract, userAddress } = getDependenciesStrict(provider)
  const [ooki, iooki, vbzrx, slp_ooki_eth] = await stakingContract
    .balanceOfByAssets(userAddress)
    .callAsync()
  return { ooki, iooki, vbzrx, slp_ooki_eth }
}

/**
 * Get addresses of the stakeable tokens.
 * Throws an error if there is a missing address
 */
function getStakeableAddresses(chainId: ChainId) {
  return {
    ooki: assetUtils.getAddress(chainId, Asset.OOKI),
    vbzrx: assetUtils.getAddress(chainId, Asset.vBZRX),
    iooki: assetUtils.getAddress(chainId, Asset.IOOKI),
    slp_ooki_eth: assetUtils.getAddress(chainId, Asset.SLP_OOKI_ETH),
  }
}

/**
 * Get the staking specific tokens balances from a user wallet
 * @param userAddress user wallet
 */
async function getStakeableBalances(provider: FulcrumProvider) {
  const { helperContract, chainId, userAddress } = getDependenciesStrict(provider)
  const addresses = getStakeableAddresses(chainId)
  const [ooki, vbzrx, iooki, slp_ooki_eth] = await helperContract
    .balanceOf(
      [addresses.ooki, addresses.vbzrx, addresses.iooki, addresses.slp_ooki_eth],
      userAddress
    )
    .callAsync()

  return { ooki, vbzrx, iooki, slp_ooki_eth }
}

/**
 * Get the spending allowance for each stakeable asset for a user wallet
 * @param userAddress user wallet
 */
async function getStakeableAllowances(provider: FulcrumProvider) {
  const { helperContract, chainId, userAddress } = getDependenciesStrict(provider)
  const addresses = getStakeableAddresses(chainId)
  const [ooki, vbzrx, iooki, slp_ooki_eth] = await helperContract
    .allowance(
      [addresses.ooki, addresses.vbzrx, addresses.iooki, addresses.slp_ooki_eth],
      userAddress,
      stakingConfig.stakingContract
    )
    .callAsync()

  return { ooki, vbzrx, iooki, slp_ooki_eth }
}

/**
 * Send stake request.
 * Amounts should be in decimal base.
 * eg: if user wants to unstake 10 OOKI then ooki argument should be new BigNumber(10)
 * @param tokenAmounts amount of ooki, vbzrx, iooki and slp to stake
 */
function stake(provider: FulcrumProvider, tokenAmounts: Map<stakeableToken, BigNumber>) {
  const { stakingContract, chainId } = getDependenciesStrict(provider)
  const stakeableAddresses = getStakeableAddresses(chainId)

  const { addresses, amounts } = Array.from(tokenAmounts).reduce(
    (acc, [token, amount]) => {
      acc.amounts.push(amount.times(10 ** 18))
      acc.addresses.push(stakeableAddresses[token])
      return acc
    },
    { addresses: [] as string[], amounts: [] as BigNumber[] }
  )

  const tx = stakingContract.stake(addresses, amounts)
  return providerUtils.runTx(provider, tx, stakingContract)
}

/**
 * Send unstake request.
 * Amounts should be in decimal base.
 * eg: if user wants to unstake 10 OOKI then ooki argument should be new BigNumber(10)
 * @param tokens amount of ooki, vbzrx and slp to stake
 */
function unstake(provider: FulcrumProvider, tokenAmounts: Map<unstakeableToken, BigNumber>) {
  const { stakingContract, chainId } = getDependenciesStrict(provider)
  const stakeableAddresses = getStakeableAddresses(chainId)

  const { addresses, amounts } = Array.from(tokenAmounts).reduce(
    (acc, [token, amount]) => {
      acc.amounts.push(amount.times(10 ** 18))
      acc.addresses.push(stakeableAddresses[token])
      return acc
    },
    { addresses: [] as string[], amounts: [] as BigNumber[] }
  )

  const tx = stakingContract.unstake(addresses, amounts)
  return providerUtils.runTx(provider, tx, stakingContract)
}

/**
 * Claims 3CRV and OOKI
 */
function claimStakingRewards(provider: FulcrumProvider, shouldRestake = false) {
  const { stakingContract } = getDependenciesStrict(provider)
  const tx = stakingContract.claim(shouldRestake)
  return providerUtils.runTx(provider, tx, stakingContract)
}

function claimSushiRewards(provider: FulcrumProvider) {
  const { stakingContract } = getDependenciesStrict(provider)
  const tx = stakingContract.claimSushi()
  return providerUtils.runTx(provider, tx, stakingContract)
}

async function getVotingPower(provider: FulcrumProvider) {
  const { stakingContract, userAddress } = getDependenciesStrict(provider)
  const votingPowerStaked = await stakingContract.votingFromStakedBalanceOf(userAddress).callAsync()
  const votingPowerTotal = await stakingContract.votingBalanceOfNow(userAddress).callAsync()
  return {
    votingPowerStaked: votingPowerStaked.div(10 ** 18),
    votingPowerTotal: votingPowerTotal.div(10 ** 18),
  }
}

async function getGlobalStaked(provider: FulcrumProvider) {
  const { contractsSource } = providerUtils.getDependenciesStrictRead(provider)
  const { stakingContract, sushiPoolIdSLP } = stakingConfig
  const masterChefSushi = contractsSource.getMasterChefSushiContract()
  const globalStaked = await providerUtils.getWalletBalances(provider, stakingContract, [
    Asset.OOKI,
    Asset.vBZRX,
    Asset.IOOKI,
  ])
  const inSushi = await masterChefSushi
    .userInfo(new BigNumber(sushiPoolIdSLP), stakingContract)
    .callAsync()

  globalStaked.push({ token: Asset.SLP_OOKI_ETH, amount: inSushi.amount.div(10 ** 18) })
  return globalStaked
}

function getDelegate(provider: FulcrumProvider) {
  const { voteDelegatorContract, userAddress } = getDependenciesStrict(provider)
  return voteDelegatorContract.delegates(userAddress).callAsync()
}

function delegate(provider: FulcrumProvider, delegateAddress: string) {
  const { voteDelegatorContract } = getDependenciesStrict(provider)
  const tx = voteDelegatorContract.delegate(delegateAddress)
  return providerUtils.runTx(provider, tx, voteDelegatorContract)
}

export default {
  claimStakingRewards,
  claimSushiRewards,
  claimVestedBZRX,
  delegate,
  getDelegate,
  getGlobalStaked,
  getStakeableAddresses,
  getStakeableAllowances,
  getStakeableBalances,
  getStakedBalances,
  getStakingRewards,
  getVestedBzrxBalance,
  getVestedOokiInRewards,
  getVotingPower,
  setStakingAllowance,
  stake,
  unstake,
  verifyStake,
}
